import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingButton } from "@/components/shared/LoadingButton";
import { StateCard } from "@/components/shared/StateCard";
import {
  formatPricingCategoryLabel,
  formatPricingUnitLabel,
  getApprovalPricingDisplay,
  type ApprovalPricingDisplay,
} from "@/lib/approval-pricing";
import { formatCurrency, mapApiTypeToLabel } from "@/lib/tickets";
import { downloadPublicApprovalPdf, fetchPublicApproval, submitPublicApprovalDecision, type PublicApprovalView } from "@/lib/public-approval";
import {
  CheckCircle2,
  XCircle,
  HelpCircle,
  Download,
  Paperclip,
  FileText,
  DollarSign,
  Pen,
  Image,
  AlertTriangle,
  ShieldCheck,
} from "lucide-react";

type Decision = null | "APPROVED" | "REJECTED" | "CLARIFICATION_REQUESTED";
type SignaturePoint = { x: number; y: number };

const DATE_FORMAT = new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" });

function formatCompactNumber(value: number) {
  if (!Number.isFinite(value)) return "";
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, "");
}

function formatApprovalLineItemQty(item: PublicApprovalView["lineItems"][number]) {
  const quantity = Number(item.quantity ?? 0);
  if (!Number.isFinite(quantity) || quantity <= 0) return "";

  const formattedQuantity = formatCompactNumber(quantity);
  const category = String(item.category ?? "").trim().toUpperCase();
  const rawUnit = String(item.unit ?? "").trim();
  const normalizedUnit = rawUnit.toLowerCase();

  const isLabor = category === "LABOR" || category === "WORK";
  if (isLabor) {
    if (["fixed", "fixed price", "lump sum", "lump-sum", "ls"].includes(normalizedUnit)) {
      return "";
    }
    if (["hrs", "hr", "hour", "hours", "h", "", "qty", "quantity"].includes(normalizedUnit)) {
      return `${formattedQuantity}h`;
    }
    if (
      quantity === 1 &&
      ["ea", "each", "unit"].includes(normalizedUnit)
    ) {
      return "";
    }
  }

  if (!rawUnit || ["qty", "quantity"].includes(normalizedUnit)) {
    return formattedQuantity;
  }

  if (quantity === 1 && ["ea", "each", "unit"].includes(normalizedUnit)) {
    return formattedQuantity;
  }

  return `${formattedQuantity} ${rawUnit}`;
}

function formatPublicEstimateQuantity(row: Extract<ApprovalPricingDisplay, { kind: "itemized" }>["rows"][number]) {
  const quantity = row.unit === "fixed" && Number(row.quantity || 0) <= 0 ? 1 : Number(row.quantity || 0);
  if (!Number.isFinite(quantity) || quantity <= 0) return "1";
  return formatCompactNumber(quantity);
}

function formatChangeOrderReason(value?: string | null) {
  if (value === "client_request") return "Client request";
  if (value === "unforeseen_condition") return "Unforeseen condition";
  if (value === "material_change") return "Material change";
  if (value === "scope_change") return "Scope change";
  if (value === "code_permit_requirement") return "Code / permit requirement";
  if (value === "other") return "Other";
  return value ?? "";
}

function formatChangeOrderType(value?: string | null) {
  if (value === "add_cost") return "Add cost";
  if (value === "credit_deduct") return "Credit / deduct";
  if (value === "no_cost_change") return "No cost change";
  return value ?? "";
}

function formatExtraWorkReason(value?: string | null) {
  if (value === "client_request") return "Client request";
  if (value === "additional_scope") return "Additional scope";
  if (value === "emergency_urgent_work") return "Emergency / urgent work";
  if (value === "site_condition") return "Site condition";
  if (value === "after_hours_work") return "After-hours work";
  if (value === "material_service_add_on") return "Material/service add-on";
  if (value === "other") return "Other";
  return value ?? "";
}

function formatSignedCurrencyWithPlus(value: number) {
  if (value > 0) return `+${formatCurrency(value)}`;
  if (value < 0) return `-${formatCurrency(Math.abs(value))}`;
  return formatCurrency(0);
}

export default function ExternalApproval() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approval, setApproval] = useState<PublicApprovalView | null>(null);

  const [decision, setDecision] = useState<Decision>(null);
  const [submitted, setSubmitted] = useState<{
    decision: string;
    decidedAt: string;
    referenceNumber: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const [signerName, setSignerName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [signerCompany, setSignerCompany] = useState("");
  const [comment, setComment] = useState("");

  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureStrokes, setSignatureStrokes] = useState<SignaturePoint[][]>([]);
  const [signatureError, setSignatureError] = useState("");
  const [signatureDataUrl, setSignatureDataUrl] = useState("");
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const activeStrokeRef = useRef<SignaturePoint[]>([]);
  const lastPointRef = useRef<SignaturePoint | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!token) return;
      setLoading(true);
      setError(null);
      setApproval(null);
      setDecision(null);
      setSubmitted(null);
      setComment("");
      setSignerTitle("");
      setSignatureDataUrl("");
      setSignatureStrokes([]);
      try {
        const data = await fetchPublicApproval(token);
        setApproval(data);
        setSignerName(data.recipientName || "");
        setSignerCompany(data.client?.name || "");
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not open approval link.");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [token]);

  const pricingDisplay = useMemo(() => (approval ? getApprovalPricingDisplay(approval) : null), [approval]);
  const isChangeOrder = approval?.ticketType === "CHANGE_ORDER";
  const isExtraWork = approval?.ticketType === "EXTRA_WORK";
  const changeOrderSummary = useMemo(() => {
    if (!approval || approval.ticketType !== "CHANGE_ORDER") return null;
    const metadata = approval.pricingMetadata?.changeOrder;
    const reason = metadata?.reasonForChangeLabel || formatChangeOrderReason(metadata?.reasonForChange);
    const changeType = metadata?.changeTypeLabel || formatChangeOrderType(metadata?.changeType);
    const amount = approval.projectFinancialContext?.currentTicketTotal ?? approval.total;
    return { reason, changeType, amount };
  }, [approval]);
  const extraWorkDetails = useMemo(() => {
    if (!approval || approval.ticketType !== "EXTRA_WORK") return null;
    const metadata = approval.pricingMetadata?.extraWork;
    const reason = metadata?.reasonLabel || formatExtraWorkReason(metadata?.reason);
    return reason ? { reason } : null;
  }, [approval]);
  const hasLineItemImages = useMemo(
    () => Boolean(pricingDisplay?.kind === "legacy" && approval?.lineItems.some((item) => Boolean(item.thumbnailUrl))),
    [approval, pricingDisplay],
  );
  const hasItemizedImages = useMemo(
    () => Boolean(pricingDisplay?.kind === "itemized" && pricingDisplay.rows.some((row) => Boolean(row.thumbnailUrl))),
    [pricingDisplay],
  );
  const formatSignedCurrency = useCallback(
    (value: number) => `${value < 0 ? "-" : ""}${formatCurrency(Math.abs(value))}`,
    [],
  );

  const setupSignatureCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.floor(rect.width * ratio);
    canvas.height = Math.floor(rect.height * ratio);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#111827";
  }, []);

  const redrawSignatureStrokes = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    for (const stroke of signatureStrokes) {
      if (stroke.length === 0) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i += 1) ctx.lineTo(stroke[i].x, stroke[i].y);
      ctx.stroke();
    }
  }, [signatureStrokes]);

  useEffect(() => {
    if (!signatureModalOpen) return;
    setupSignatureCanvas();
    redrawSignatureStrokes();
    const onResize = () => {
      setupSignatureCanvas();
      redrawSignatureStrokes();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [signatureModalOpen, setupSignatureCanvas, redrawSignatureStrokes]);

  useEffect(() => {
    if (!signatureModalOpen) return;
    redrawSignatureStrokes();
  }, [signatureStrokes, signatureModalOpen, redrawSignatureStrokes]);

  const getPointFromEvent = (event: React.PointerEvent<HTMLCanvasElement>): SignaturePoint => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const onSignaturePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(event.pointerId);
    drawingRef.current = true;
    const point = getPointFromEvent(event);
    activeStrokeRef.current = [point];
    lastPointRef.current = point;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const onSignaturePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const point = getPointFromEvent(event);
    activeStrokeRef.current.push(point);
    const prev = lastPointRef.current;
    const ctx = canvas.getContext("2d");
    if (ctx && prev) {
      ctx.beginPath();
      ctx.moveTo(prev.x, prev.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
    }
    lastPointRef.current = point;
  };

  const finishSignatureStroke = (event?: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (activeStrokeRef.current.length > 0) {
      const stroke = [...activeStrokeRef.current];
      setSignatureStrokes((prev) => [...prev, stroke]);
    }
    if (event && canvasRef.current) {
      try {
        if (canvasRef.current.hasPointerCapture(event.pointerId)) {
          canvasRef.current.releasePointerCapture(event.pointerId);
        }
      } catch {
        // ignore pointer capture release errors to avoid dropping the stroke
      }
    }
    activeStrokeRef.current = [];
    lastPointRef.current = null;
  };

  const clearSignature = () => {
    setSignatureStrokes([]);
    setSignatureDataUrl("");
    setSignatureError("");
    activeStrokeRef.current = [];
    lastPointRef.current = null;
    redrawSignatureStrokes();
  };

  const handleDownloadPdf = async () => {
    if (!token) return;
    setDownloadingPdf(true);
    try {
      await downloadPublicApprovalPdf(token);
    } catch (e) {
      setSignatureError(e instanceof Error ? e.message : "PDF download failed.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const decisionLabel = useMemo(() => {
    if (decision === "APPROVED") return "Approval";
    if (decision === "REJECTED") return "Rejection";
    if (decision === "CLARIFICATION_REQUESTED") return "Clarification Request";
    return "Decision";
  }, [decision]);

  const submitDecision = async () => {
    if (!token || !approval || !decision) return;
    if (!signerName.trim()) {
      setSignatureError("Name is required.");
      return;
    }
    if (decision === "APPROVED" && signatureRequiredForApproval && !signatureDataUrl) {
      setSignatureError("Signature required to approve.");
      return;
    }
    if ((decision === "REJECTED" || decision === "CLARIFICATION_REQUESTED") && !comment.trim()) {
      setSignatureError("Comment is required for this decision.");
      return;
    }

    setIsSubmitting(true);
    setSignatureError("");
    try {
      const result = await submitPublicApprovalDecision(token, {
        decision,
        signerName: signerName.trim(),
        signerTitle: signerTitle.trim() || undefined,
        signerCompany: signerCompany.trim() || undefined,
        comment: comment.trim() || undefined,
        signatureDataUrl: signatureDataUrl || undefined,
      });
      setSubmitted({
        decision: result.decision,
        decidedAt: result.decidedAt,
        referenceNumber: result.referenceNumber,
      });
      setApproval({
        ...approval,
        decision: result.decision,
        decidedAt: result.decidedAt,
      });
      if (decision === "APPROVED") {
        await handleDownloadPdf();
      }
    } catch (e) {
      setSignatureError(e instanceof Error ? e.message : "Submission failed. Please retry.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <p className="text-sm text-muted-foreground">Loading approval link...</p>
      </div>
    );
  }

  if (error) {
    const lower = error.toLowerCase();
    if (lower.includes("expired")) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <StateCard
            icon={AlertTriangle}
            tone="warning"
            title="Approval link expired"
            description="This link has expired. Request a fresh approval link from the sender."
          />
        </div>
      );
    }
    if (lower.includes("already")) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <StateCard
            icon={AlertTriangle}
            tone="warning"
            title="Decision already submitted"
            description="This approval link has already been used."
          />
        </div>
      );
    }
    if (lower.includes("invalid")) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <StateCard
            icon={XCircle}
            tone="error"
            title="Invalid approval link"
            description="This link is invalid or unavailable."
          />
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <StateCard
          icon={AlertTriangle}
          tone="error"
          title="Network error"
          description={error}
          primaryActionLabel="Retry"
          onPrimaryAction={() => window.location.reload()}
        />
      </div>
    );
  }

  if (!approval) return null;

  const signatureRequiredForApproval = approval.requireSignatureToApprove;
  const canApprove = !signatureRequiredForApproval || Boolean(signatureDataUrl);

  const decided = submitted || (approval.decision && approval.decidedAt
    ? { decision: approval.decision, decidedAt: approval.decidedAt, referenceNumber: approval.referenceNumber }
    : null);

  if (decided) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4 animate-scale-in">
          <div className="rounded-full bg-status-approved-bg p-4 inline-flex">
            <CheckCircle2 className="h-10 w-10 text-success" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Decision Recorded</h1>
          <p className="text-muted-foreground">
            Your response for {approval.ticketNumber} was recorded on {DATE_FORMAT.format(new Date(decided.decidedAt))}.
          </p>
          <p className="text-xs text-muted-foreground">Reference: {decided.referenceNumber}</p>
          <Button variant="outline" className="gap-1.5 mt-4" onClick={() => void handleDownloadPdf()} disabled={downloadingPdf}>
            <Download className="h-4 w-4" /> {downloadingPdf ? "Generating..." : "Download PDF Copy"}
          </Button>
        </div>
      </div>
    );
  }

  const projectTotalImpactSection = approval.projectFinancialContext?.showRollup ? (
    <div className="rounded-lg border bg-card p-5 space-y-4 animate-fade-in">
      <div className="space-y-1">
        <h2 className="font-semibold text-foreground">Project Total Impact</h2>
        <p className="text-sm text-muted-foreground">
          {approval.ticketType === "CHANGE_ORDER"
            ? "This Change Order adjusts the approved contract total for this project."
            : `You already approved work on this project. This ${mapApiTypeToLabel(approval.ticketType)} will be added on top of that approved amount.`}
        </p>
      </div>
      {approval.projectFinancialContext.previousApprovedTickets.length > 0 ? (
        <div className="space-y-2">
          {approval.projectFinancialContext.previousApprovedTickets.map((ticket) => (
            <div key={ticket.id} className="flex items-start justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {ticket.ticketNumber} · {ticket.title}
                </p>
                <p className="text-xs text-muted-foreground">
                  {mapApiTypeToLabel(ticket.type)} · {toDateShort(ticket.approvedAt)}
                </p>
              </div>
              <p className="shrink-0 text-sm font-semibold text-foreground">
                {formatCurrency(ticket.totalAmount)}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
          No previously approved tickets were found for this project.
        </div>
      )}
      {approval.projectFinancialContext.mode === "CHANGE_ORDER" ? (
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-md border bg-background px-3 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Current approved total</p>
            <p className="mt-1 text-lg font-semibold text-foreground">
              {formatCurrency(approval.projectFinancialContext.previousApprovedTotal)}
            </p>
          </div>
          <div className="rounded-md border bg-background px-3 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">This Change Order</p>
            <p className={`mt-1 text-lg font-semibold ${
              approval.projectFinancialContext.currentTicketTotal < 0 ? "text-destructive" : "text-accent"
            }`}>
              {formatSignedCurrencyWithPlus(approval.projectFinancialContext.currentTicketTotal)}
            </p>
          </div>
          <div className="rounded-md border border-accent/30 bg-accent/5 px-3 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">New contract total</p>
            <p className="mt-1 text-lg font-semibold text-accent">
              {formatCurrency(approval.projectFinancialContext.revisedProjectTotal)}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-md border bg-background px-3 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Previously approved</p>
            <p className="mt-1 text-lg font-semibold text-foreground">
              {formatCurrency(approval.projectFinancialContext.previousApprovedTotal)}
            </p>
          </div>
          <div className="rounded-md border bg-background px-3 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">This ticket</p>
            <p className="mt-1 text-lg font-semibold text-foreground">
              {formatSignedCurrency(approval.projectFinancialContext.currentTicketTotal)}
            </p>
          </div>
          <div className="rounded-md border border-accent/30 bg-accent/5 px-3 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">New project total</p>
            <p className="mt-1 text-lg font-semibold text-accent">
              {formatCurrency(approval.projectFinancialContext.revisedProjectTotal)}
            </p>
          </div>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 rounded bg-accent/20 text-accent flex items-center justify-center font-bold">
              {approval.contractor.name.slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <span className="text-sm font-bold text-foreground block truncate">{approval.contractor.name}</span>
              <span className="text-xs text-muted-foreground flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Secure approval via WorkScope</span>
            </div>
          </div>
          <Button variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={() => void handleDownloadPdf()} disabled={downloadingPdf}>
            <Download className="h-3.5 w-3.5" /> {downloadingPdf ? "Generating..." : "PDF"}
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8 space-y-5">
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
            <FileText className="h-3.5 w-3.5" />
            <span>{mapApiTypeToLabel(approval.ticketType)}</span>
            <span>•</span>
            <span>{approval.ticketNumber}</span>
            <span>•</span>
            <span>{toDateShort(approval.sentAt)}</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground">{approval.title}</h1>
          <p className="text-sm text-muted-foreground">{approval.project?.name ?? "No project"}</p>
        </div>

        {approval.latestCompanyResponse?.message ? (
          <div className="rounded-lg border bg-card p-5 space-y-2">
            <h2 className="font-semibold text-foreground">Latest Company Response</h2>
            <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
              {approval.latestCompanyResponse.message}
            </p>
            <p className="text-xs text-muted-foreground">
              {toDateShort(approval.latestCompanyResponse.createdAt)}
              {approval.latestCompanyResponse.authorName || approval.latestCompanyResponse.authorEmail
                ? ` · ${approval.latestCompanyResponse.authorName ?? approval.latestCompanyResponse.authorEmail}`
                : ""}
            </p>
          </div>
        ) : null}

        {approval.showDescriptionToCustomer ? (
          <div className="rounded-lg border bg-card p-5 space-y-3">
            <h2 className="font-semibold text-foreground">Work Description</h2>
            <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
              {approval.description || "No description provided."}
            </p>
          </div>
        ) : null}

        {changeOrderSummary ? (
          <div className="rounded-lg border bg-card p-5 space-y-4 animate-fade-in">
            <div>
              <h2 className="font-semibold text-foreground">Change Order Summary</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Review what changed and the amount this Change Order adds or credits.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-[1fr_1fr_1.15fr]">
              {changeOrderSummary.reason ? (
                <div className="rounded-md border bg-muted/25 px-3 py-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Reason for change</p>
                  <p className="mt-1 text-sm font-semibold text-foreground">{changeOrderSummary.reason}</p>
                </div>
              ) : null}
              {changeOrderSummary.changeType ? (
                <div className="rounded-md border bg-muted/25 px-3 py-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Change type</p>
                  <p className="mt-1 text-sm font-semibold text-foreground">{changeOrderSummary.changeType}</p>
                </div>
              ) : null}
              <div className={`rounded-md border px-3 py-3 ${
                changeOrderSummary.amount < 0
                  ? "border-destructive/30 bg-destructive/5"
                  : "border-accent/30 bg-accent/5"
              }`}>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">This Change Order</p>
                <p className={`mt-1 text-2xl font-bold ${
                  changeOrderSummary.amount < 0 ? "text-destructive" : "text-accent"
                }`}>
                  {formatSignedCurrencyWithPlus(changeOrderSummary.amount)}
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {extraWorkDetails ? (
          <div className="rounded-lg border bg-card p-5 space-y-3 animate-fade-in">
            <h2 className="font-semibold text-foreground">Extra Work Details</h2>
            <div className="rounded-md border bg-muted/25 px-3 py-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Reason</p>
              <p className="mt-1 text-sm font-semibold text-foreground">
                {extraWorkDetails.reason}
              </p>
            </div>
          </div>
        ) : null}

        {!isExtraWork ? projectTotalImpactSection : null}

        {pricingDisplay && !(isChangeOrder && pricingDisplay.kind === "final") ? (
          <div className="rounded-lg border bg-card overflow-hidden">
            {pricingDisplay.kind === "final" ? (
              <>
                <div className="p-5 space-y-4">
                  <h2 className="font-semibold text-foreground flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" /> Total Amount
                  </h2>
                  <div className="rounded-md border border-primary/20 bg-primary/5 px-4 py-5">
                    <p className="text-sm font-medium text-muted-foreground">Total Amount</p>
                    <p className="mt-1 text-3xl font-bold text-primary">{formatCurrency(pricingDisplay.total)}</p>
                  </div>
                  {pricingDisplay.showMetadataTotals &&
                  pricingDisplay.metadata &&
                  (pricingDisplay.metadata.discountAmount > 0 || pricingDisplay.metadata.taxAmount > 0) ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span className="font-medium text-foreground">{formatCurrency(pricingDisplay.metadata.customerPrice)}</span>
                      </div>
                      {pricingDisplay.metadata.discountAmount > 0 ? (
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-muted-foreground">Discount</span>
                          <span className="font-medium text-foreground">
                            -{formatCurrency(pricingDisplay.metadata.discountAmount)}
                          </span>
                        </div>
                      ) : null}
                      {pricingDisplay.metadata.taxAmount > 0 ? (
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-muted-foreground">Tax</span>
                          <span className="font-medium text-foreground">{formatCurrency(pricingDisplay.metadata.taxAmount)}</span>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}

            {pricingDisplay.kind === "summary" ? (
              <>
                <div className="p-5 pb-3">
                  <h2 className="font-semibold text-foreground flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" /> Price Summary
                  </h2>
                </div>
                <div className="px-5 pb-4 space-y-2">
                  {pricingDisplay.entries.map((entry) => (
                    <div key={entry.category} className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2">
                      <span className="text-sm font-medium text-foreground">{entry.label}</span>
                      <span className="text-sm font-semibold text-foreground">{formatCurrency(entry.total)}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-primary px-5 py-4 flex items-center justify-between">
                  <span className="text-primary-foreground font-semibold">
                    {isChangeOrder ? "This Change Order" : "Total Amount"}
                  </span>
                  <span className="text-2xl font-bold text-primary-foreground">
                    {isChangeOrder
                      ? formatSignedCurrencyWithPlus(changeOrderSummary?.amount ?? pricingDisplay.total)
                      : formatCurrency(pricingDisplay.total)}
                  </span>
                </div>
              </>
            ) : null}

            {pricingDisplay.kind === "itemized" ? (
              <>
                <div className="p-5 pb-3">
                  <h2 className="font-semibold text-foreground flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" /> Itemized Breakdown
                  </h2>
                </div>
                <div className="px-5 pb-3 overflow-x-auto">
                  <table className={`w-full text-sm ${hasItemizedImages ? "min-w-[740px]" : "min-w-[680px]"}`}>
                    <thead>
                      <tr className="border-b">
                        {hasItemizedImages ? (
                          <th className="text-left py-2 font-medium text-muted-foreground">Image</th>
                        ) : null}
                        <th className="text-left py-2 font-medium text-muted-foreground">Description</th>
                        <th className="text-left py-2 font-medium text-muted-foreground">Category</th>
                        <th className="text-right py-2 font-medium text-muted-foreground">Qty</th>
                        <th className="text-right py-2 font-medium text-muted-foreground">Unit</th>
                        <th className="text-right py-2 font-medium text-muted-foreground">Rate</th>
                        <th className="text-right py-2 font-medium text-muted-foreground">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pricingDisplay.rows.map((row) => (
                        <tr key={row.id} className="border-b last:border-0">
                          {hasItemizedImages ? (
                            <td className="py-2.5">
                              {row.thumbnailUrl ? (
                                <img src={row.thumbnailUrl} alt="" className="h-9 w-9 rounded border object-cover" />
                              ) : null}
                            </td>
                          ) : null}
                          <td className="py-2.5 text-foreground">{row.description}</td>
                          <td className="py-2.5 text-muted-foreground">
                            <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                              {formatPricingCategoryLabel(row.category)}
                            </span>
                          </td>
                          <td className="py-2.5 text-right text-muted-foreground">{formatPublicEstimateQuantity(row)}</td>
                          <td className="py-2.5 text-right text-muted-foreground">{formatPricingUnitLabel(row.unit)}</td>
                          <td className="py-2.5 text-right text-muted-foreground">{formatCurrency(row.unitCost)}</td>
                          <td className="py-2.5 text-right font-medium text-foreground">{formatCurrency(row.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-primary px-5 py-4 flex items-center justify-between">
                  <span className="text-primary-foreground font-semibold">
                    {isChangeOrder ? "This Change Order" : "Total Amount"}
                  </span>
                  <span className="text-2xl font-bold text-primary-foreground">
                    {isChangeOrder
                      ? formatSignedCurrencyWithPlus(changeOrderSummary?.amount ?? pricingDisplay.total)
                      : formatCurrency(pricingDisplay.total)}
                  </span>
                </div>
              </>
            ) : null}

            {pricingDisplay.kind === "legacy" ? (
              <>
                <div className="p-5 pb-3">
                  <h2 className="font-semibold text-foreground flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" /> Cost Breakdown
                  </h2>
                </div>
                <div className="px-5 pb-3 overflow-x-auto">
                  <table className={`w-full text-sm ${hasLineItemImages ? "min-w-[540px]" : "min-w-[480px]"}`}>
                    <thead>
                      <tr className="border-b">
                        {hasLineItemImages ? (
                          <th className="text-left py-2 font-medium text-muted-foreground">Image</th>
                        ) : null}
                        <th className="text-left py-2 font-medium text-muted-foreground">Description</th>
                        <th className="text-right py-2 font-medium text-muted-foreground">Qty</th>
                        <th className="text-right py-2 font-medium text-muted-foreground">Rate</th>
                        <th className="text-right py-2 font-medium text-muted-foreground">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {approval.lineItems.map((item) => (
                        <tr key={item.id} className="border-b last:border-0">
                          {hasLineItemImages ? (
                            <td className="py-2.5">
                              {item.thumbnailUrl ? (
                                <img src={item.thumbnailUrl} alt="" className="h-9 w-9 rounded border object-cover" />
                              ) : (
                                <span className="text-xs text-muted-foreground">-</span>
                              )}
                            </td>
                          ) : null}
                          <td className="py-2.5 text-foreground">
                            {item.description}
                            {item.direction === "DEDUCT" ? (
                              <span className="ml-2 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                                Credit
                              </span>
                            ) : null}
                          </td>
                          <td className="py-2.5 text-right text-muted-foreground">{formatApprovalLineItemQty(item)}</td>
                          <td className="py-2.5 text-right text-muted-foreground">
                            {formatSignedCurrency(
                              item.direction === "DEDUCT" ? -Math.abs(item.unitCost) : Math.abs(item.unitCost),
                            )}
                          </td>
                          <td className="py-2.5 text-right font-medium text-foreground">{formatSignedCurrency(item.totalCost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-primary px-5 py-4 flex items-center justify-between">
                  <span className="text-primary-foreground font-semibold">
                    {isChangeOrder ? "This Change Order" : "Total Amount"}
                  </span>
                  <span className="text-2xl font-bold text-primary-foreground">
                    {isChangeOrder
                      ? formatSignedCurrencyWithPlus(changeOrderSummary?.amount ?? pricingDisplay.total)
                      : formatCurrency(pricingDisplay.total)}
                  </span>
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        {isExtraWork ? projectTotalImpactSection : null}

        {approval.showAttachmentsToCustomer ? (
          <div className="rounded-lg border bg-card p-5 space-y-3">
            <h2 className="font-semibold text-foreground flex items-center gap-2">
              <Paperclip className="h-4 w-4 text-muted-foreground" /> Attachments ({approval.attachments.length})
            </h2>
            {approval.attachments.length === 0 ? (
              <p className="text-sm text-muted-foreground">No attachments.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {approval.attachments.map((att) => (
                  <a key={att.id} href={att.url} target="_blank" rel="noreferrer" className="rounded-lg bg-muted aspect-video flex flex-col items-center justify-center p-2 text-center cursor-pointer hover:bg-muted/70 transition-colors relative">
                    <Image className="h-5 w-5 text-muted-foreground mb-1" />
                    <span className="text-xs text-muted-foreground">{att.fileName}</span>
                    <span className="absolute top-1 right-1 text-[9px] bg-card px-1 py-0.5 rounded border text-muted-foreground">{att.tag}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        ) : null}

        {!decision && (
          <div className="rounded-lg border-2 border-accent/30 bg-card p-5 sm:p-6 space-y-4 animate-fade-in">
            <h2 className="font-semibold text-foreground text-center text-lg">Your Decision</h2>
            <p className="text-sm text-muted-foreground text-center">Review the details above and choose your action</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <Button
                className="bg-success text-success-foreground hover:bg-success/90 gap-2 h-12 text-base font-semibold"
                onClick={() => setDecision("APPROVED")}
                disabled={!canApprove}
              >
                <CheckCircle2 className="h-5 w-5" /> Approve
              </Button>
              <Button variant="outline" className="border-destructive text-destructive hover:bg-destructive/10 gap-2 h-12 text-base font-semibold" onClick={() => setDecision("REJECTED")}>
                <XCircle className="h-5 w-5" /> Reject
              </Button>
              <Button variant="outline" className="gap-2 h-12 text-base" onClick={() => setDecision("CLARIFICATION_REQUESTED")}>
                <HelpCircle className="h-5 w-5" /> Clarify
              </Button>
            </div>
            {signatureRequiredForApproval && !signatureDataUrl ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center space-y-2">
                <p className="text-sm font-medium text-amber-900">Signature required to approve.</p>
                <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setSignatureModalOpen(true)}>
                  <Pen className="h-3.5 w-3.5" /> Capture signature
                </Button>
              </div>
            ) : null}
          </div>
        )}

        {decision && (
          <div className="rounded-lg border bg-card p-5 sm:p-6 space-y-4 animate-fade-in-up">
            <h2 className="font-semibold text-foreground">Confirm {decisionLabel}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Your Name *</Label>
                <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Full name" />
              </div>
              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input value={signerTitle} onChange={(e) => setSignerTitle(e.target.value)} placeholder="Project Manager" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Company</Label>
              <Input value={signerCompany} onChange={(e) => setSignerCompany(e.target.value)} placeholder="Company name" />
            </div>
            <div className="space-y-1.5">
              <Label>
                Comment {(decision === "REJECTED" || decision === "CLARIFICATION_REQUESTED") ? <span className="text-destructive">*</span> : <span className="text-xs text-muted-foreground font-normal">(optional)</span>}
              </Label>
              <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder={decision === "REJECTED" ? "Reason for rejection..." : decision === "CLARIFICATION_REQUESTED" ? "What needs clarification..." : "Optional comment..."} rows={3} />
            </div>
            {decision === "APPROVED" && (
              <div className="space-y-1.5">
                <Label>
                  Signature {signatureRequiredForApproval ? <span className="text-destructive">*</span> : <span className="text-xs text-muted-foreground font-normal">(optional)</span>}
                </Label>
                {!signatureDataUrl ? (
                  <button type="button" className="w-full border-2 border-dashed rounded-lg p-8 bg-muted/30 text-center hover:border-accent/50 transition-colors" onClick={() => setSignatureModalOpen(true)}>
                    <Pen className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      {signatureRequiredForApproval ? "Capture required signature" : "Capture signature"}
                    </p>
                  </button>
                ) : (
                  <div className="rounded-lg border border-success/30 bg-success/5 p-4 text-center">
                    <img src={signatureDataUrl} alt="Signature" className="max-h-20 mx-auto" />
                    <p className="text-xs text-muted-foreground mt-1">Signature captured</p>
                    <Button variant="ghost" size="sm" className="mt-2 h-7 text-xs" onClick={() => setSignatureModalOpen(true)}>
                      Replace Signature
                    </Button>
                  </div>
                )}
              </div>
            )}
            {signatureError ? <p className="text-sm text-destructive">{signatureError}</p> : null}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setDecision(null)} disabled={isSubmitting}>Back</Button>
              <LoadingButton
                className={`flex-1 font-semibold h-11 ${decision === "APPROVED" ? "bg-success text-success-foreground hover:bg-success/90" : "bg-accent text-accent-foreground hover:bg-accent/90"}`}
                isLoading={isSubmitting}
                loadingLabel={decision === "APPROVED" ? "Submitting and downloading PDF..." : "Submitting..."}
                onClick={submitDecision}
                disabled={isSubmitting || (decision === "APPROVED" && signatureRequiredForApproval && !signatureDataUrl)}
              >
                {decision === "APPROVED" ? "Download PDF and Submit Approval" : `Submit ${decisionLabel}`}
              </LoadingButton>
            </div>
          </div>
        )}
      </div>

      {signatureModalOpen ? (
        <div className="fixed inset-0 bg-foreground/30 z-50 flex items-center justify-center p-4" onClick={() => setSignatureModalOpen(false)}>
          <div className="w-full max-w-xl rounded-lg border bg-card p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold">Capture Signature</h3>
            <div className="rounded-md border bg-background h-44 relative touch-none" style={{ touchAction: "none" }}>
              <canvas
                ref={canvasRef}
                className="w-full h-full"
                onPointerDown={onSignaturePointerDown}
                onPointerMove={onSignaturePointerMove}
                onPointerUp={finishSignatureStroke}
                onPointerCancel={finishSignatureStroke}
                onPointerLeave={finishSignatureStroke}
              />
              {signatureStrokes.length === 0 ? (
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-sm text-muted-foreground">
                  Sign here
                </div>
              ) : null}
            </div>
            <div className="flex gap-2 justify-between">
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={clearSignature}>Clear</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setSignatureStrokes((prev) => prev.slice(0, -1))} disabled={signatureStrokes.length === 0}>
                  Undo
                </Button>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setSignatureModalOpen(false)}>Cancel</Button>
                <Button
                  type="button"
                  size="sm"
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  onClick={() => {
                    if (signatureStrokes.length === 0) {
                      setSignatureError("Please add a signature or cancel.");
                      return;
                    }
                    const canvas = canvasRef.current;
                    if (!canvas) return;
                    setSignatureDataUrl(canvas.toDataURL("image/png"));
                    setSignatureError("");
                    setSignatureModalOpen(false);
                  }}
                >
                  Save Signature
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function toDateShort(value?: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
}
